// =============================================================================
// UI Feature Config service (React port)
//
// Ports the behaviour of the Angular S3UiFeatureConfigRepository +
// UiFeatureConfigService pair:
//   src/app/views/pages/saas-admin/ui-features/services/*
//
// The real AWS API is called for both load (GET) and save (PATCH), with the
// same 409-conflict "reload version and retry once" logic. The endpoint sits
// behind the Lambda's Cognito JWT check, so the signed-in user's ID token is
// attached as `Authorization: Bearer <token>`. If no one is signed in, the
// header is omitted and the API answers 401/403, which the UI surfaces as
// "Unauthorized user" — exactly like the original.
// =============================================================================
import { CompanySize, UiFeatureConfig } from '../models/uiFeatureConfig';
import { getIdToken } from '../../../services/cognitoAuth';

/**
 * Build request headers, including the Cognito Bearer token when a user is
 * signed in. Editing (PATCH) requires the caller to be in the `saas-admin`
 * Cognito group; the backend enforces that and returns 403 otherwise.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = await getIdToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

// Base URL comes from the env (with the original AWS endpoint as the default).
const API_ROOT =
  (process.env.REACT_APP_FEATURE_CONFIG_API_URL as string | undefined) ??
  'https://5x3dj6j8a2.execute-api.ap-south-1.amazonaws.com';

const BASE = `${API_ROOT}/v1/feature-config`;

/** Error thrown by the service; carries the HTTP status + any API error code. */
export class FeatureConfigError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'FeatureConfigError';
  }
}

async function parseError(res: Response): Promise<FeatureConfigError> {
  let body: any = null;
  try { body = await res.json(); } catch { /* non-JSON body */ }
  const code = body?.error?.code || body?.code;
  const message =
    body?.error?.message || body?.message || `Request failed (${res.status})`;
  return new FeatureConfigError(message, res.status, code);
}

/** GET the stored config for a company size. */
export async function loadConfig(companySize: CompanySize): Promise<UiFeatureConfig> {
  const res = await fetch(`${BASE}/${companySize}`, {
    method: 'GET',
    headers: await authHeaders(),
  });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

/** PATCH the config; on a 409 version conflict, reload + retry once. */
export async function saveConfig(config: UiFeatureConfig): Promise<void> {
  await patch(config, config.version, false);
}

async function patch(
  config: UiFeatureConfig,
  expectedVersion: number,
  isRetry: boolean,
): Promise<void> {
  const body = { expectedVersion, changes: config.features };
  const res = await fetch(`${BASE}/${config.companySize}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });

  if (res.ok) {
    const updated: UiFeatureConfig = await res.json();
    // Keep the in-memory config current so the next save has the right version.
    config.version = updated.version;
    config.features = updated.features;
    return;
  }

  if (res.status === 409 && !isRetry) {
    // Someone else saved in between: refresh the version and retry once.
    const latest = await loadConfig(config.companySize);
    await patch(config, latest.version, true);
    return;
  }

  throw await parseError(res);
}

// ---- pure helpers (ported from UiFeatureConfigService) ----------------------

export function isEnabled(config: UiFeatureConfig | null | undefined, guid: string): boolean {
  return !!(config && config.features && config.features[guid]);
}

export function enabledGuids(config: UiFeatureConfig | null | undefined): string[] {
  if (!config || !config.features) return [];
  return Object.keys(config.features).filter(g => config.features[g]);
}

export function setEnabled(config: UiFeatureConfig, guid: string, enabled: boolean): void {
  if (!config.features) config.features = {};
  config.features[guid] = enabled;
}
